import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { AppliedDiscount } from '../types';
import { useCartStore } from '../store/cartStore';

type DiscountStatus = 'idle' | 'validating' | 'applied' | 'error';

interface UseDiscountResult {
    discount: AppliedDiscount | null;
    isApplying: boolean;
    status: DiscountStatus;
    message: string | null;
    error: string | null;
    applyCode: (code: string, silent?: boolean) => Promise<void>;
    removeCode: () => void;
}

export function useDiscount(): UseDiscountResult {
    const cart = useCartStore(state => state.cart);
    const applyDiscount = useCartStore(state => state.applyDiscount);
    const clearDiscount = useCartStore(state => state.clearDiscount);
    const setDiscountError = useCartStore(state => state.setDiscountError);
    const discountError = useCartStore(state => state.discountError);

    const [status, setStatus] = useState<DiscountStatus>('idle');
    const [message, setMessage] = useState<string | null>(null);
    const [isApplying, setIsApplying] = useState(false);

    const discount = cart.discount ?? null;

    const applyCode = useCallback(async (code: string, silent: boolean = false) => {
        const trimmed = code.trim().toUpperCase();
        if (trimmed.length === 0) {
            if (!silent) {
                setDiscountError('Enter a discount code to apply.');
                setStatus('error');
            }
            return;
        }

        if (!silent) {
            setStatus('validating');
            setDiscountError(null);
            setMessage(null);
        }
        setIsApplying(true);

        try {
            const response = await apiService.validateDiscount(trimmed, cart.totals.subtotal);
            const applied: AppliedDiscount = {
                code: response.code,
                percentage: response.percentage,
                fixedAmount: response.fixedAmount,
                minOrderOverride: response.minOrderOverride,
                amount: response.discountAmount,
                validatedSubtotal: response.validatedSubtotal ?? cart.totals.subtotal,
                validatedAt: new Date().toISOString(),
                isStale: false,
                message: response.message
            };

            applyDiscount(applied);
            if (!silent) {
                setMessage(response.message ?? 'Discount applied!');
                setStatus('applied');
            }
        } catch (error) {
            // Clear the invalid discount to prevent infinite revalidation loops
            clearDiscount();
            if (!silent) {
                const errorMessage = error instanceof Error ? error.message : 'We couldn\'t apply that code. Please try again or use another code.';
                setDiscountError(errorMessage);
                setStatus('error');
            }
        } finally {
            setIsApplying(false);
        }
    }, [applyDiscount, clearDiscount, cart.totals.subtotal, setDiscountError]);

    const removeCode = useCallback(() => {
        clearDiscount();
        setDiscountError(null);
        setMessage(null);
        setStatus('idle');
    }, [clearDiscount, setDiscountError]);

    // Track if we've validated on mount
    const hasValidatedOnMount = useRef(false);

    // Validate discount on mount - if there's a stored discount code, verify it's still valid
    useEffect(() => {
        const storedCode = cart.discount?.code;
        if (!storedCode || hasValidatedOnMount.current || isApplying) {
            return;
        }

        hasValidatedOnMount.current = true;
        // Silently validate - if invalid, just remove without error
        void applyCode(storedCode, true);
    }, [cart.discount?.code, applyCode, isApplying]);

    // Re-validate when discount becomes stale (cart contents changed)
    useEffect(() => {
        const pendingCode = cart.discount?.code;
        if (!pendingCode || !cart.discount?.isStale || isApplying) {
            return;
        }

        // Use silent mode for auto-revalidation - if invalid, just remove without error
        void applyCode(pendingCode, true);
    }, [cart.discount?.isStale, cart.discount?.code, applyCode, isApplying]);

    return {
        discount,
        isApplying,
        status,
        message,
        error: discountError,
        applyCode,
        removeCode
    };
}
